import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { SignDetail } from 'src/app/model/sign/sign-detail';
import { SignTitleAdjustment } from 'src/app/model/sign/title/sign-title-adjustment';
import { cloneDeep } from 'lodash';
import {
  ModalService,
  ObservableHandlerPair,
} from 'src/app/services/modal.service';
import { SignTitleAdjustmentEditComponent } from '../../edit-association/title/sign-title-adjustment-edit.component';
import { SignTitleAdjustmentAsset } from 'src/app/model/sign/title/sign-title-adjustment-asset';
import { Lightbox } from 'ngx-lightbox';
import { SignTitleAdjustmentControllerRequestService } from 'src/app/services/api/sign-associations/implementations/sign-title-adjustment-controller-request.service';
import { SubscriptionWithDestroyTriggerComponent } from 'src/app/subscription-helpers/subscription-with-destroy-trigger.component';
import { SignTitleAdjustmentOverview } from 'src/app/model/sign/title/sign-title-adjustment-overview';
import { of, Subject } from 'rxjs';
import { catchError, startWith, switchMap, takeUntil } from 'rxjs/operators';
import { INT_MAX_SIGNED_32_BIT_VALUE } from 'src/globals/globals';
import { ProperPageResult } from 'src/app/model/proper-page-result';

@Component({
  selector: 'sign-title-adjustment-overview',
  templateUrl: './sign-title-adjustment-overview.component.html',
  styleUrls: [
    './sign-title-adjustment-overview.component.scss',
    '../sign-associations-overview-shared-style.scss',
  ],
})
export class SignTitleAdjustmentOverviewComponent
  extends SubscriptionWithDestroyTriggerComponent
  implements OnInit, OnDestroy
{
  @Input()
  sign: SignDetail;

  loading: boolean = false;
  signTitleAdjustments: SignTitleAdjustmentOverview[];

  private getSignTitleAdjustmentSubject: Subject<any>;

  constructor(
    private modalService: ModalService,
    private signTitleAdjustmentControllerRequestService: SignTitleAdjustmentControllerRequestService,
    private _lightbox: Lightbox
  ) {
    super();
  }

  ngOnInit(): void {
    this.getSignTitleAdjustmentSubject = new Subject<any>();

    this.addSubscription(
      this.getSignTitleAdjustmentSubject.pipe(
        startWith({}),
        switchMap(async () => {
          this.loading = true;
          return await this.signTitleAdjustmentControllerRequestService.getPaged(
            this.sign.id,
            null,
            'id',
            true,
            INT_MAX_SIGNED_32_BIT_VALUE,
            0,
            false
          );
        }),
        catchError(() => {
          return of({});
        }),
        takeUntil(this.destroyThisComponent)
      ),
      (res: ProperPageResult<SignTitleAdjustmentOverview>) => {
        this.signTitleAdjustments = res == null ? [] : res.items;
        this.loading = false;
      }
    );
  }

  ngOnDestroy(): void {
    super.ngOnDestroy();
    if (this.getSignTitleAdjustmentSubject != null) {
      this.getSignTitleAdjustmentSubject.complete();
    }
  }

  addItem(): void {
    this.openEditModal();
  }

  editItem(signTitleAdjustment: SignTitleAdjustment): void {
    this.openEditModal(signTitleAdjustment);
  }

  private openEditModal(signTitleAdjustment?: SignTitleAdjustment): void {
    const assocObject =
      signTitleAdjustment == null ? null : cloneDeep(signTitleAdjustment);
    this.modalService.openComponentInModal(
      SignTitleAdjustmentEditComponent,
      [
        { key: 'signId', value: this.sign.id },
        { key: 'assocObject', value: assocObject },
      ],
      new ObservableHandlerPair(
        (modalRef) => modalRef.componentInstance.saveSuccessEvent,
        (res: SignTitleAdjustment) => {
          if (res != null) {
            this.getSignTitleAdjustmentSubject.next();
          }
        }
      ),
      { width: '750px', maxHeight: '90vh' }
    );
  }

  deleteItem(signTitleAdjustment: SignTitleAdjustment): void {
    this.modalService.confirmDialog(
      'Titel aanpassing verwijderen',
      'Bent u zeker dat u deze titel aanpassing wilt verwijderen?',
      (b: boolean) => {
        if (b) {
          this.performDelete(signTitleAdjustment);
        }
      }
    );
  }

  private async performDelete(
    signTitleAdjustment: SignTitleAdjustment
  ): Promise<void> {
    this.loading = true;
    try {
      await this.signTitleAdjustmentControllerRequestService.archive(
        signTitleAdjustment.id
      );
      this.getSignTitleAdjustmentSubject.next();
    } catch (err) {
      this.loading = false;
      throw err;
    }
  }

  openLightbox(signTitleAdjustmentAssets: SignTitleAdjustmentAsset[]) {
    let albums = [];
    for (let asset of signTitleAdjustmentAssets) {
      if (asset.archived) {
        continue;
      }
      const src = '/api/assets/' + asset.asset.uuid;
      const caption = 'Ontwerp';
      const thumb = '/api/assets/' + asset.asset.uuid;
      const album = {
        src: src,
        caption: caption,
        thumb: thumb,
      };

      albums.push(album);
    }
    this._lightbox.open(albums, 0);
  }
}
